import React, { createContext, useContext, useState } from 'react';

/* ═══════════════════════════════════════════════════════
   Thèmes — 3 palettes pour l'interface Madjeo SIG
═══════════════════════════════════════════════════════ */
export const THEMES = {
  geo: {
    id: 'geo', label: 'SIG Nuit', swatch: '#22c55e',
    vars: {
      // Expert layout (Dashboard.css / Sidebar.css via tokens.css vars)
      '--ink-750':           '#050a18',
      '--ink-600':           '#0a1020',
      '--ink-500':           '#0d1528',
      '--ink-400':           '#101b32',
      '--ink-300':           '#162040',
      '--ink-200':           '#1c2850',
      '--cyan-400':          '#22d3ee',
      '--cyan-500':          '#06b6d4',
      '--cyan-600':          '#0891b2',
      '--cyan-glow':         'rgba(6,182,212,0.28)',
      '--cyan-glow-sm':      'rgba(6,182,212,0.15)',
      '--indigo-500':        '#667eea',
      '--indigo-400':        '#818cf8',
      '--indigo-glow':       'rgba(102,126,234,0.28)',
      // Design-system semantic vars (component CSS files)
      '--ds-panel-rgb':      '7, 13, 30',
      '--ds-input-rgb':      '5, 10, 24',
      '--ds-well-rgb':       '3, 7, 18',
      '--ds-sep-rgb':        '255, 255, 255',
      '--ds-accent-rgb':     '6, 182, 212',
      '--ds-indigo-rgb':     '129, 140, 248',
      '--ds-text':           '#e2e8f0',
      '--ds-text-2':         '#94a3b8',
      '--ds-text-dk':        '#334155',
      '--ds-text-ph':        '#1e3a5f',
      // Layout (AdminGeoLayout.css)
      '--ag-green':          '#22c55e',
      '--ag-green-dk':       '#16a34a',
      '--ag-green-glow':     'rgba(34,197,94,0.18)',
      '--ag-amber':          '#f59e0b',
      '--ag-red':            '#ef4444',
      '--ag-bg':             '#040d18',
      '--ag-surface':        'rgba(5,14,30,0.97)',
      '--ag-surface-2':      'rgba(8,20,42,0.94)',
      '--ag-border':         'rgba(34,197,94,0.12)',
      '--ag-border-dk':      'rgba(34,197,94,0.22)',
      '--ag-text':           '#e2f0e8',
      '--ag-text-muted':     '#64887a',
      '--ag-text-dim':       '#2a4a38',
      '--ag-topbar-bg':      'rgba(4,13,24,0.98)',
      '--ag-statusbar-bg':   'rgba(3,9,18,0.98)',
      '--ag-accent-hover':   'rgba(34,197,94,0.07)',
      '--ag-accent-actv':    'rgba(34,197,94,0.12)',
      '--ag-danger-hover':   'rgba(239,68,68,0.08)',
      // Composants (inline styles)
      '--c-bg':              '#040d18',
      '--c-surface':         'rgba(5,14,30,0.97)',
      '--c-surface2':        'rgba(8,20,42,0.94)',
      '--c-border':          'rgba(34,197,94,0.14)',
      '--c-border-dk':       'rgba(34,197,94,0.28)',
      '--c-accent':          '#22c55e',
      '--c-accent-dk':       '#16a34a',
      '--c-amber':           '#f59e0b',
      '--c-red':             '#ef4444',
      '--c-blue':            '#60a5fa',
      '--c-text':            '#e2f0e8',
      '--c-muted':           '#64887a',
      '--c-dim':             '#2a4a38',
      '--c-glow':            'rgba(34,197,94,0.12)',
      '--c-overlay':         'rgba(4,13,24,0.88)',
      '--c-bg-60':           'rgba(4,13,24,0.60)',
      '--c-bg-80':           'rgba(4,13,24,0.80)',
      // Alpha accent (pour les fonds semi-transparents)
      '--c-a04':  'rgba(34,197,94,0.04)',
      '--c-a05':  'rgba(34,197,94,0.05)',
      '--c-a06':  'rgba(34,197,94,0.06)',
      '--c-a07':  'rgba(34,197,94,0.07)',
      '--c-a08':  'rgba(34,197,94,0.08)',
      '--c-a10':  'rgba(34,197,94,0.10)',
      '--c-a12':  'rgba(34,197,94,0.12)',
      '--c-a14':  'rgba(34,197,94,0.14)',
      '--c-a15':  'rgba(34,197,94,0.15)',
      '--c-a20':  'rgba(34,197,94,0.20)',
      '--c-a25':  'rgba(34,197,94,0.25)',
      '--c-a28':  'rgba(34,197,94,0.28)',
      '--c-a30':  'rgba(34,197,94,0.30)',
      '--c-a35':  'rgba(34,197,94,0.35)',
      '--c-a40':  'rgba(34,197,94,0.40)',
    },
  },

  dark: {
    id: 'dark', label: 'Sombre', swatch: '#a78bfa',
    vars: {
      // Expert layout — violet accents, ultra-dark backgrounds
      '--ink-750':           '#030308',
      '--ink-600':           '#07070f',
      '--ink-500':           '#0d0d18',
      '--ink-400':           '#12121f',
      '--ink-300':           '#171726',
      '--ink-200':           '#1c1c2e',
      '--cyan-400':          '#a78bfa',
      '--cyan-500':          '#8b5cf6',
      '--cyan-600':          '#7c3aed',
      '--cyan-glow':         'rgba(139,92,246,0.28)',
      '--cyan-glow-sm':      'rgba(139,92,246,0.15)',
      '--indigo-500':        '#a78bfa',
      '--indigo-400':        '#c4b5fd',
      '--indigo-glow':       'rgba(167,139,250,0.28)',
      // Design-system semantic vars
      '--ds-panel-rgb':      '7, 7, 18',
      '--ds-input-rgb':      '5, 5, 14',
      '--ds-well-rgb':       '3, 3, 10',
      '--ds-sep-rgb':        '255, 255, 255',
      '--ds-accent-rgb':     '139, 92, 246',
      '--ds-indigo-rgb':     '167, 139, 250',
      '--ds-text':           '#e2e8f0',
      '--ds-text-2':         '#c4b5fd',
      '--ds-text-dk':        '#4b5563',
      '--ds-text-ph':        '#374151',
      '--ag-green':          '#a78bfa',
      '--ag-green-dk':       '#7c3aed',
      '--ag-green-glow':     'rgba(167,139,250,0.18)',
      '--ag-amber':          '#fbbf24',
      '--ag-red':            '#f87171',
      '--ag-bg':             '#0d0d14',
      '--ag-surface':        'rgba(15,15,28,0.98)',
      '--ag-surface-2':      'rgba(22,22,40,0.96)',
      '--ag-border':         'rgba(139,92,246,0.14)',
      '--ag-border-dk':      'rgba(139,92,246,0.28)',
      '--ag-text':           '#e2e8f0',
      '--ag-text-muted':     '#6b7280',
      '--ag-text-dim':       '#374151',
      '--ag-topbar-bg':      'rgba(10,10,18,0.99)',
      '--ag-statusbar-bg':   'rgba(6,6,12,0.99)',
      '--ag-accent-hover':   'rgba(139,92,246,0.07)',
      '--ag-accent-actv':    'rgba(139,92,246,0.12)',
      '--ag-danger-hover':   'rgba(248,113,113,0.08)',
      '--c-bg':              '#0d0d14',
      '--c-surface':         'rgba(15,15,28,0.98)',
      '--c-surface2':        'rgba(22,22,40,0.96)',
      '--c-border':          'rgba(139,92,246,0.14)',
      '--c-border-dk':       'rgba(139,92,246,0.28)',
      '--c-accent':          '#a78bfa',
      '--c-accent-dk':       '#7c3aed',
      '--c-amber':           '#fbbf24',
      '--c-red':             '#f87171',
      '--c-blue':            '#60a5fa',
      '--c-text':            '#e2e8f0',
      '--c-muted':           '#6b7280',
      '--c-dim':             '#374151',
      '--c-glow':            'rgba(167,139,250,0.12)',
      '--c-overlay':         'rgba(13,13,20,0.90)',
      '--c-bg-60':           'rgba(13,13,20,0.60)',
      '--c-bg-80':           'rgba(13,13,20,0.80)',
      '--c-a04':  'rgba(139,92,246,0.04)',
      '--c-a05':  'rgba(139,92,246,0.05)',
      '--c-a06':  'rgba(139,92,246,0.06)',
      '--c-a07':  'rgba(139,92,246,0.07)',
      '--c-a08':  'rgba(139,92,246,0.08)',
      '--c-a10':  'rgba(139,92,246,0.10)',
      '--c-a12':  'rgba(139,92,246,0.12)',
      '--c-a14':  'rgba(139,92,246,0.14)',
      '--c-a15':  'rgba(139,92,246,0.15)',
      '--c-a20':  'rgba(139,92,246,0.20)',
      '--c-a25':  'rgba(139,92,246,0.25)',
      '--c-a28':  'rgba(139,92,246,0.28)',
      '--c-a30':  'rgba(139,92,246,0.30)',
      '--c-a35':  'rgba(139,92,246,0.35)',
      '--c-a40':  'rgba(139,92,246,0.40)',
    },
  },

  light: {
    id: 'light', label: 'Clair', swatch: '#0d9488',
    vars: {
      // Expert layout — light backgrounds, teal accents
      '--ink-750':           '#e8eff5',
      '--ink-600':           '#f8fafc',
      '--ink-500':           '#f1f5f9',
      '--ink-400':           '#e2e8f0',
      '--ink-300':           '#cbd5e1',
      '--ink-200':           '#b0bec5',
      '--cyan-400':          '#0d9488',
      '--cyan-500':          '#0f766e',
      '--cyan-600':          '#115e59',
      '--cyan-glow':         'rgba(13,148,136,0.28)',
      '--cyan-glow-sm':      'rgba(13,148,136,0.15)',
      '--indigo-500':        '#0891b2',
      '--indigo-400':        '#38bdf8',
      '--indigo-glow':       'rgba(8,145,178,0.28)',
      // Design-system semantic vars
      '--ds-panel-rgb':      '255, 255, 255',
      '--ds-input-rgb':      '248, 250, 252',
      '--ds-well-rgb':       '241, 245, 249',
      '--ds-sep-rgb':        '0, 0, 0',
      '--ds-accent-rgb':     '13, 148, 136',
      '--ds-indigo-rgb':     '13, 148, 136',
      '--ds-text':           '#0f172a',
      '--ds-text-2':         '#334155',
      '--ds-text-dk':        '#94a3b8',
      '--ds-text-ph':        '#cbd5e1',
      '--ag-green':          '#0d9488',
      '--ag-green-dk':       '#0f766e',
      '--ag-green-glow':     'rgba(13,148,136,0.20)',
      '--ag-amber':          '#b45309',
      '--ag-red':            '#dc2626',
      '--ag-bg':             '#f0f4f8',
      '--ag-surface':        'rgba(255,255,255,0.98)',
      '--ag-surface-2':      'rgba(241,245,249,0.97)',
      '--ag-border':         'rgba(13,148,136,0.16)',
      '--ag-border-dk':      'rgba(13,148,136,0.32)',
      '--ag-text':           '#0f172a',
      '--ag-text-muted':     '#475569',
      '--ag-text-dim':       '#94a3b8',
      '--ag-topbar-bg':      'rgba(248,250,252,0.99)',
      '--ag-statusbar-bg':   'rgba(241,245,249,0.99)',
      '--ag-accent-hover':   'rgba(13,148,136,0.07)',
      '--ag-accent-actv':    'rgba(13,148,136,0.12)',
      '--ag-danger-hover':   'rgba(220,38,38,0.08)',
      '--c-bg':              '#f0f4f8',
      '--c-surface':         'rgba(255,255,255,0.98)',
      '--c-surface2':        'rgba(241,245,249,0.97)',
      '--c-border':          'rgba(13,148,136,0.16)',
      '--c-border-dk':       'rgba(13,148,136,0.32)',
      '--c-accent':          '#0d9488',
      '--c-accent-dk':       '#0f766e',
      '--c-amber':           '#b45309',
      '--c-red':             '#dc2626',
      '--c-blue':            '#2563eb',
      '--c-text':            '#0f172a',
      '--c-muted':           '#475569',
      '--c-dim':             '#94a3b8',
      '--c-glow':            'rgba(13,148,136,0.12)',
      '--c-overlay':         'rgba(15,23,42,0.75)',
      '--c-bg-60':           'rgba(240,244,248,0.60)',
      '--c-bg-80':           'rgba(240,244,248,0.80)',
      '--c-a04':  'rgba(13,148,136,0.04)',
      '--c-a05':  'rgba(13,148,136,0.05)',
      '--c-a06':  'rgba(13,148,136,0.06)',
      '--c-a07':  'rgba(13,148,136,0.07)',
      '--c-a08':  'rgba(13,148,136,0.08)',
      '--c-a10':  'rgba(13,148,136,0.10)',
      '--c-a12':  'rgba(13,148,136,0.12)',
      '--c-a14':  'rgba(13,148,136,0.14)',
      '--c-a15':  'rgba(13,148,136,0.15)',
      '--c-a20':  'rgba(13,148,136,0.20)',
      '--c-a25':  'rgba(13,148,136,0.25)',
      '--c-a28':  'rgba(13,148,136,0.28)',
      '--c-a30':  'rgba(13,148,136,0.30)',
      '--c-a35':  'rgba(13,148,136,0.35)',
      '--c-a40':  'rgba(13,148,136,0.40)',
    },
  },
};

/* ── Application des vars CSS sur :root ─────────────────────── */
function applyTheme(themeId) {
  const theme = THEMES[themeId] || THEMES.geo;
  const root  = document.documentElement;
  Object.entries(theme.vars).forEach(([k, v]) => root.style.setProperty(k, v));
  root.setAttribute('data-theme', themeId);
}

// Application immédiate (avant le premier render React) pour éviter le flash
const _savedTheme = localStorage.getItem('madjeo-theme') || 'geo';
applyTheme(_savedTheme);

/* ── Context ─────────────────────────────────────────────────── */
const ThemeContext = createContext(null);

export function ThemeProvider({ children }) {
  const [themeId, setThemeId] = useState(_savedTheme);

  const setTheme = (id) => {
    if (!THEMES[id]) return;
    applyTheme(id);
    localStorage.setItem('madjeo-theme', id);
    setThemeId(id);
  };

  return (
    <ThemeContext.Provider value={{ themeId, setTheme, themes: THEMES }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used inside ThemeProvider');
  return ctx;
}
